import React from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useSEO } from "@/hooks/useSEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ExternalLink } from 'lucide-react';
import { dataSources } from '@/data/demographicData';

const DataSources = () => {
  useSEO({
    title: 'Data Sources - Female Partner Calculator | Census & Survey Data',
    description: 'Explore the authoritative data sources behind our compatibility calculations including US Census Bureau, CDC NHANES, and Pew Research Center statistics.',
    keywords: 'data sources, US Census Bureau, CDC NHANES, Pew Research, demographic data, government statistics',
    canonical: 'https://femalepartnercalculator.com/data-sources'
  });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <Breadcrumbs />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">
              Data Sources & Citations
            </h1>
            <p className="text-xl text-muted-foreground">
              Comprehensive list of authoritative sources used in our compatibility calculations
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Government Data Sources</CardTitle>
              <CardDescription>
                Official statistics from federal agencies and research institutions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {dataSources.map((source) => (
                  <div key={source.id} className="border-b border-border pb-6 last:border-b-0">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <h3 className="font-semibold text-lg">
                        [{source.id}] {source.title}
                      </h3>
                      <Badge variant="secondary" className="shrink-0">
                        Government
                      </Badge>
                    </div>
                    <p className="text-muted-foreground mb-4 leading-relaxed">
                      {source.description}
                    </p>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-primary hover:underline text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Source
                    </a>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Data Quality & Methodology</CardTitle>
              <CardDescription>
                How we ensure accuracy and reliability in our statistical calculations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Source Verification</h4>
                <p className="text-muted-foreground text-sm">
                  All data sources are verified government agencies or peer-reviewed research institutions.
                  We prioritize Census Bureau data, CDC surveys, and established research centers.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Data Freshness</h4>
                <p className="text-muted-foreground text-sm">
                  We use the most recent data available, typically within 1-3 years of collection.
                  Government surveys often have 1-2 year publication delays after data collection.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Sample Sizes</h4>
                <p className="text-muted-foreground text-sm">
                  All statistics are based on large, representative samples (typically 10,000+ respondents)
                  to ensure statistical reliability and minimize sampling error.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Key Sources Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                  <div>
                  <h4 className="font-semibold mb-3">U.S. Census Bureau</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• American Community Survey (ACS)</li>
                    <li>• Current Population Survey (CPS)</li>
                    <li>• Educational Attainment Tables (B15002)</li>
                    <li>• Marital Status Tables (B12002)</li>
                    <li>• Decennial Census data</li>
                    <li>• Population estimates</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-3">Centers for Disease Control</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• NHANES health surveys</li>
                    <li>• Behavioral Risk Factor Surveillance</li>
                    <li>• National health statistics</li>
                    <li>• BMI and obesity data</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-3">Pew Research Center</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Political affiliation surveys</li>
                    <li>• Social trends research</li>
                    <li>• Religious landscape studies</li>
                    <li>• Demographic analysis</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-3">Additional Sources</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Bureau of Labor Statistics</li>
                    <li>• General Social Survey (GSS)</li>
                    <li>• YouGov polling data</li>
                    <li>• Academic research papers</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Data Limitations</CardTitle>
              <CardDescription>
                Important considerations when interpreting our statistical results
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <h4 className="font-semibold mb-2 text-yellow-800 dark:text-yellow-200">Temporal Lag</h4>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  Government data typically has 1-2 year delays between collection and publication.
                  Our calculations reflect demographic patterns from recent years, not real-time data.
                </p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="font-semibold mb-2 text-blue-800 dark:text-blue-200">Geographic Variation</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  National averages may not reflect local demographic patterns.
                  Urban vs. rural areas can have significantly different characteristics.
                </p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                <h4 className="font-semibold mb-2 text-purple-800 dark:text-purple-200">Individual Variation</h4>
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  Statistical probabilities describe population patterns, not individual outcomes.
                  Personal networks, social circles, and individual traits affect real-world compatibility.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="text-center">
            <p className="text-muted-foreground">
              Questions about our data sources? Check our <a href="/methodology" className="text-primary hover:underline">methodology page</a> or <a href="/faq" className="text-primary hover:underline">FAQ section</a>
            </p>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default DataSources;